import { useState } from "react";
import { Pressable, Text, View, type ViewStyle } from "react-native";
import { ChevronDown, type LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";
import { MoonCountryPicker } from "./MoonCountryPicker";
import {
  COUNTRIES,
  getCountryName,
  type CountryMetadata,
} from "@src/packages/location/countryMetadata";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  label: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  labelHint?: string;
  /** ISO-3166 alpha-2 on record, or null when nothing is chosen yet. */
  value: string | null;
  locale: "en" | "fr";
  onChange: (country: CountryMetadata) => void;
  /** What the box says while nothing is chosen. */
  placeholder: string;
  searchPlaceholder: string;
  noMatchesMessage: (query: string) => string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  testID?: string;
  /** Stable analytics identifier for the question this field answers. */
  action?: string;
};

/**
 * A country as a form field: the shared label and box, the flag and the
 * localised name inside, and the searchable list dropping down beneath when
 * tapped — the same shape as the phone field's country, standing on its
 * own. The box reads as focused while the list is open.
 */
export function MoonCountryField({
  label,
  labelIcon,
  labelHint,
  value,
  locale,
  onChange,
  placeholder,
  searchPlaceholder,
  noMatchesMessage,
  hint,
  error,
  disabled,
  loading,
  style,
  testID,
  action,
}: Props) {
  const fontSize = useInputFontSize();
  const capturePress = usePressCapture("MoonCountryField");
  const [open, setOpen] = useState(false);

  const selected = value
    ? (COUNTRIES.find((country) => country.code === value) ?? null)
    : null;
  const selectedName = selected ? getCountryName(selected, locale) : null;

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      labelHint={labelHint}
      hint={hint}
      error={error}
      disabled={disabled}
      loading={loading}
      testID={testID}
      style={style}
    >
      <View>
        <MoonFieldInput hasError={!!error} focused={open} paddingY={12}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`${label}: ${selectedName ?? placeholder}`}
            disabled={disabled}
            onPress={() => {
              capturePress(
                pressLabel(selectedName, label),
                action ?? "open_country_field",
              );
              setOpen((current) => !current);
            }}
            style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
          >
            {selected ? (
              <Text style={{ fontSize: 16 }} accessibilityElementsHidden>
                {selected.flag}
              </Text>
            ) : null}
            <Text
              style={{
                flex: 1,
                fontFamily: typography.families.sans,
                fontSize,
                color: selected ? colors.text : colors.textFaint,
              }}
            >
              {selectedName ?? placeholder}
            </Text>
            <ChevronDown size={16} color={colors.textFaint} strokeWidth={1.5} />
          </Pressable>
        </MoonFieldInput>
        <MoonCountryPicker
          open={open}
          selectedCode={value}
          locale={locale}
          onSelect={(country) => {
            onChange(country);
            setOpen(false);
          }}
          searchPlaceholder={searchPlaceholder}
          noMatchesMessage={noMatchesMessage}
          searchTestID={testID ? `${testID}-search` : undefined}
        />
      </View>
    </MoonFormField>
  );
}
